import axios, { AxiosResponse } from 'axios';
import {
  DashboardCSAData,
  VulnerabilidadesResponse,
  VulnerabilidadeCSA,
  FiltrosVulnerabilidades,
  HealthCheckResponse,
  DashboardStatsResponse,
  VulnerabilidadesListResponse,
  VulnerabilidadeDetailsResponse,
  HealthResponse,
  CSAError,
  CSAExportOptions,
  CSAReportData
} from '../../types/dashboard/csa.types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const CSA_API_PREFIX = '/api/dashboard/governanca/csa';

const csaApi = axios.create({
  baseURL: `${API_BASE_URL}${CSA_API_PREFIX}`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
});

csaApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

csaApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Classe principal do serviço CSA
 */
class CSAService {
  formatarScoreCVSS(cvssScore: string): any {
    throw new Error('Method not implemented.');
  }
  padronizarHost(host: string, ip: string): any {
    throw new Error('Method not implemented.');
  }
  padronizarCVE(cve: string): any {
    throw new Error('Method not implemented.');
  }

  /**
   * Obter estatísticas consolidadas do dashboard CSA
   */
  async getDashboardStats(clienteId: string): Promise<DashboardCSAData> {
    try {
      this.validateClienteId(clienteId);
      
      const response: AxiosResponse<DashboardCSAData> = await csaApi.get('/stats', {
        params: { cliente_id: clienteId }
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao carregar estatísticas do dashboard CSA');
    }
  }

  /**
   * Obter lista de vulnerabilidades com filtros e paginação
   */
  async getVulnerabilidades(
    clienteId: string, 
    filtros: FiltrosVulnerabilidades = {}
  ): Promise<VulnerabilidadesResponse> {
    try {
      this.validateClienteId(clienteId);
      
      const params: any = {
        cliente_id: clienteId,
        page: filtros.page || 1,
        limit: filtros.limit || 10,
        sortBy: filtros.sortBy || 'created_at',
        sortOrder: filtros.sortOrder || 'desc'
      };

      if (filtros.severity && filtros.severity.trim() !== '') {
        params.severity = filtros.severity.trim();
      }
      
      if (filtros.host && filtros.host.trim() !== '') {
        params.host = filtros.host.trim();
      }
      
      if (filtros.search && filtros.search.trim() !== '') {
        params.search = this.utils.sanitizarBusca(filtros.search);
      }

      if (filtros.cve && filtros.cve.trim() !== '') {
        params.cve = filtros.cve.trim();
      }

      if (filtros.ip && filtros.ip.trim() !== '') {
        params.ip = filtros.ip.trim();
      }

      if (filtros.port && filtros.port.trim() !== '') {
        params.port = filtros.port.trim();
      }

      if (filtros.minCvssScore !== undefined && filtros.minCvssScore !== null) {
        params.minCvssScore = filtros.minCvssScore;
      }

      const response: AxiosResponse<VulnerabilidadesResponse> = await csaApi.get('/vulnerabilidades', {
        params
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao carregar lista de vulnerabilidades');
    }
  }

  /**
   * Obter detalhes de uma vulnerabilidade específica
   */
  async getVulnerabilidade(vulnerabilidadeId: string, clienteId: string): Promise<VulnerabilidadeCSA> {
    try {
      this.validateClienteId(clienteId);
      
      if (!vulnerabilidadeId || vulnerabilidadeId.trim() === '') {
        throw new Error('ID da vulnerabilidade é obrigatório');
      }

      const response: AxiosResponse<VulnerabilidadeCSA> = await csaApi.get(
        `/vulnerabilidades/${vulnerabilidadeId}`,
        {
          params: { cliente_id: clienteId }
        }
      );
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao carregar detalhes da vulnerabilidade');
    }
  }

  /**
   * Buscar todas as vulnerabilidades sem paginação (para relatórios)
   */
  async getAllVulnerabilidades(
    clienteId: string, 
    filtros: FiltrosVulnerabilidades = {}
  ): Promise<VulnerabilidadeCSA[]> {
    try {
      this.validateClienteId(clienteId);
      
      let todasVulnerabilidades: VulnerabilidadeCSA[] = [];
      let pagina = 1;
      const limitePorPagina = 100;
      
      while (true) {
        const filtrosPaginados = {
          ...filtros,
          page: pagina,
          limit: limitePorPagina
        };
        
        const response = await this.getVulnerabilidades(clienteId, filtrosPaginados);
        const vulnerabilidadesPagina = response.vulnerabilidades;
        
        todasVulnerabilidades.push(...vulnerabilidadesPagina);
        
        if (vulnerabilidadesPagina.length < limitePorPagina) {
          break;
        }
        
        pagina++;
      }
      
      return todasVulnerabilidades;
      
    } catch (error) {
      throw this.handleError(error, 'Erro ao carregar todas as vulnerabilidades');
    }
  }

  /**
   * Health check do serviço CSA
   */
  async healthCheck(): Promise<HealthCheckResponse> {
    try {
      const response: AxiosResponse<HealthCheckResponse> = await csaApi.get('/health');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao verificar status do serviço CSA');
    }
  }

  /**
   * Exportar dados CSA
   */
  async exportarDados(
    clienteId: string, 
    opcoes: CSAExportOptions
  ): Promise<Blob> {
    try {
      this.validateClienteId(clienteId);
      
      const params = {
        cliente_id: clienteId,
        format: opcoes.format,
        includeDetails: opcoes.includeDetails,
        ...opcoes.filtros
      };

      const response = await csaApi.get('/export', {
        params,
        responseType: 'blob'
      });

      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao exportar dados CSA');
    }
  }

  /**
   * Gerar relatório CSA
   */
  async gerarRelatorio(
    clienteId: string, 
    filtros: FiltrosVulnerabilidades = {}
  ): Promise<CSAReportData> {
    try {
      this.validateClienteId(clienteId);
      
      const params = {
        cliente_id: clienteId,
        ...filtros
      };

      const response: AxiosResponse<CSAReportData> = await csaApi.get('/relatorio', {
        params
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao gerar relatório CSA');
    }
  }

  /**
   * Buscar vulnerabilidades por CVE
   */
  async buscarPorCVE(clienteId: string, cve: string): Promise<VulnerabilidadeCSA[]> {
    try {
      this.validateClienteId(clienteId);
      
      if (!cve || cve.trim() === '') {
        throw new Error('CVE é obrigatório para busca');
      }

      const response: AxiosResponse<VulnerabilidadeCSA[]> = await csaApi.get('/vulnerabilidades/cve', {
        params: { 
          cliente_id: clienteId,
          cve: cve.trim()
        }
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao buscar vulnerabilidades por CVE');
    }
  }

  /**
   * Buscar vulnerabilidades por host
   */
  async buscarPorHost(clienteId: string, host: string): Promise<VulnerabilidadeCSA[]> {
    try {
      this.validateClienteId(clienteId);
      
      if (!host || host.trim() === '') {
        throw new Error('Host é obrigatório para busca');
      }

      const filtros: FiltrosVulnerabilidades = {
        host: host.trim(),
        limit: 1000
      };

      const response = await this.getVulnerabilidades(clienteId, filtros);
      return response.vulnerabilidades;
    } catch (error) {
      throw this.handleError(error, 'Erro ao buscar vulnerabilidades por host');
    }
  }

  /**
   * Obter tendências temporais
   */
  async getTendenciasTemporais(
    clienteId: string, 
    periodo: 'semana' | 'mes' | 'trimestre' | 'ano' = 'mes'
  ): Promise<any> {
    try {
      this.validateClienteId(clienteId);
      
      const response = await csaApi.get('/tendencias', {
        params: { 
          cliente_id: clienteId,
          periodo: periodo
        }
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao carregar tendências temporais');
    }
  }

  /**
   * Obter comparativo entre períodos
   */
  async getComparativoPeriodos(
    clienteId: string,
    periodoAtual: { inicio: string; fim: string },
    periodoAnterior: { inicio: string; fim: string }
  ): Promise<any> {
    try {
      this.validateClienteId(clienteId);
      
      const response = await csaApi.get('/comparativo', {
        params: { 
          cliente_id: clienteId,
          atual_inicio: periodoAtual.inicio,
          atual_fim: periodoAtual.fim,
          anterior_inicio: periodoAnterior.inicio,
          anterior_fim: periodoAnterior.fim
        }
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao carregar comparativo entre períodos');
    }
  }

  /**
   * Obter estatísticas de um período específico
   */
  async getEstatisticasPeriodo(
    clienteId: string,
    dataInicio: string,
    dataFim: string,
    filtros?: Partial<FiltrosVulnerabilidades>
  ): Promise<any> {
    try {
      this.validateClienteId(clienteId);
      
      const params = {
        cliente_id: clienteId,
        data_inicio: dataInicio,
        data_fim: dataFim,
        ...filtros
      };

      const response = await csaApi.get('/estatisticas-periodo', {
        params
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Erro ao carregar estatísticas do período');
    }
  }

  /**
   * Validar dados antes de envio
   */
  private validateClienteId(clienteId: string): void {
    if (!clienteId || clienteId.trim() === '') {
      throw new Error('ID do cliente é obrigatório');
    }
  }

  /**
   * Tratamento centralizado de erros
   */
  private handleError(error: any, mensagemPadrao: string): Error {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          return new Error(data.message || 'Dados inválidos fornecidos');
        case 401:
          return new Error('Acesso não autorizado. Faça login novamente.');
        case 403:
          return new Error('Você não tem permissão para acessar este recurso');
        case 404:
          return new Error(data.message || 'Recurso não encontrado');
        case 422:
          return new Error(data.message || 'Dados fornecidos são inválidos');
        case 429:
          return new Error('Muitas requisições. Tente novamente em alguns momentos.');
        case 500:
          return new Error(data.message || 'Erro interno do servidor');
        case 503:
          return new Error('Serviço temporariamente indisponível');
        default:
          return new Error(data.message || mensagemPadrao);
      }
    } else if (error.request) {
      return new Error('Erro de conexão. Verifique sua internet e tente novamente.');
    } else {
      return new Error(error.message || mensagemPadrao);
    }
  }

  /**
   * Utilidades para formatação e normalização de dados
   */
  utils = {
    /**
     * Formatar score CVSS para exibição
     */
    formatarScoreCVSS: (score: string | number): string => {
      const numScore = typeof score === 'string' ? parseFloat(score) : score;
      return isNaN(numScore) ? 'N/A' : numScore.toFixed(1);
    },

    /**
     * Obter cor da severidade
     */
    getCorSeveridade: (severity: string): string => {
      const severityMap: Record<string, string> = {
        'high': '#ff4d4f',
        'medium': '#faad14',
        'low': '#52c41a',
        'info': '#1890ff'
      };
      return severityMap[severity?.toLowerCase()] || '#d9d9d9';
    },

    /**
     * Formatar data para exibição
     */
    formatarData: (data: string): string => {
      if (!data) return 'N/A';
      try {
        return new Intl.DateTimeFormat('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(new Date(data));
      } catch {
        return 'Data inválida';
      }
    },

    /**
     * Calcular tempo decorrido
     */
    calcularTempoDecorrido: (data: string): string => {
      if (!data) return 'N/A';
      
      try {
        const agora = new Date();
        const dataVuln = new Date(data);
        const diffMs = agora.getTime() - dataVuln.getTime();
        const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffDias === 0) return 'Hoje';
        if (diffDias === 1) return 'Ontem';
        if (diffDias < 7) return `${diffDias} dias atrás`;
        if (diffDias < 30) return `${Math.floor(diffDias / 7)} semanas atrás`;
        if (diffDias < 365) return `${Math.floor(diffDias / 30)} meses atrás`;
        return `${Math.floor(diffDias / 365)} anos atrás`;
      } catch {
        return 'Data inválida';
      }
    },

    /**
     * Validar e sanitizar parâmetros de busca
     */
    sanitizarBusca: (termo: string): string => {
      if (!termo) return '';
      return termo.trim().replace(/[<>]/g, '');
    },

    /**
     * Padronizar CVE para exibição
     */
    padronizarCVE: (cve: string): string => {
      if (!cve || cve.trim() === '' || cve.toLowerCase() === 'null') {
        return 'CVE-PENDENTE';
      }
      return cve.trim();
    },

    /**
     * Padronizar Host para exibição
     */
    padronizarHost: (host: string, ip?: string): string => {
      if (!host || host.trim() === '' || host.toLowerCase() === 'unknown') {
        return ip ? `IP-${ip}` : 'IP-Desconhecido';
      }
      return host.trim();
    },

    /**
     * Validar filtros antes de envio
     */
    validarFiltros: (filtros: FiltrosVulnerabilidades): string[] => {
      const erros: string[] = [];
      
      if (filtros.minCvssScore !== undefined && 
          (isNaN(Number(filtros.minCvssScore)) || 
           Number(filtros.minCvssScore) < 0 || 
           Number(filtros.minCvssScore) > 10)) {
        erros.push('Score CVSS deve ser um número entre 0 e 10');
      }
      
      if (filtros.page !== undefined && filtros.page < 1) {
        erros.push('Número da página deve ser maior que 0');
      }
      
      if (filtros.limit !== undefined && (filtros.limit < 1 || filtros.limit > 1000)) {
        erros.push('Limite deve ser entre 1 e 1000');
      }
      
      return erros;
    },

    /**
     * Construir URL de busca com filtros
     */
    construirUrlBusca: (filtros: FiltrosVulnerabilidades): string => {
      const params = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      return params.toString();
    },

    /**
     * Normalizar resposta de vulnerabilidades
     */
    normalizarVulnerabilidades: (vulnerabilidades: VulnerabilidadeCSA[]): VulnerabilidadeCSA[] => {
      return vulnerabilidades.map(vuln => ({
        ...vuln,
        cve: this.padronizarCVE(vuln.cve),
        host: this.padronizarHost(vuln.host, vuln.ip),
        cvssScore: this.formatarScoreCVSS(vuln.cvssScore)
      }));
    }
  };
}

export const csaService = new CSAService();
export default csaService;